import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import SEO from '../components/SEO';
import FadeIn from '../components/FadeIn';
import { ArrowLeft, BookOpen, Share2 } from 'lucide-react';

const GlossaryTerm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [term, setTerm] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchTerm = async () => {
            try {
                // Try fetching by slug first
                const q = query(collection(db, 'glossary'), where('slug', '==', id));
                const querySnapshot = await getDocs(q);

                if (!querySnapshot.empty) {
                    const doc = querySnapshot.docs[0];
                    setTerm({ id: doc.id, ...doc.data() });
                } else {
                    // Fallback: Try fetching by ID (for backward compatibility or draft previews)
                    const docRef = doc(db, 'glossary', id);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        setTerm({ id: docSnap.id, ...docSnap.data() });
                    } else {
                        navigate('/glossary');
                    }
                }
            } catch (error) {
                console.error("Error fetching term:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchTerm();
    }, [id, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 pt-32 flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A2CB52]"></div>
            </div>
        );
    }

    if (!term) return null;

    // Helper to clean definition (can be shared or duplicated)
    const cleanDefinition = (html) => {
        return html.replace(/^(<(p|h[1-6])>)\s*(<strong>)?Definition(<\/strong>)?\s*[:-]?\s*(<\/\2>)/i, '');
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <SEO
                title={term.metaTitle || `${term.term} - BPO Glossary`}
                description={term.metaDescription || `Definition of ${term.term} in the context of Business Process Outsourcing.`}
            />

            <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
                <FadeIn>
                    <Link
                        to="/glossary"
                        className="inline-flex items-center text-gray-500 hover:text-[#A2CB52] transition-colors mb-8 font-medium"
                    >
                        <ArrowLeft size={20} className="mr-2" />
                        Back to Glossary
                    </Link>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-8 sm:p-12">
                            <div className="flex items-center mb-8">
                                <div className="bg-[#A2CB52]/10 p-3 rounded-xl mr-4">
                                    <BookOpen size={32} className="text-[#A2CB52]" />
                                </div>
                                <h1 className="text-3xl sm:text-4xl font-bold text-[#20222D]">
                                    {term.term}
                                </h1>
                            </div>

                            <div
                                className="prose prose-lg max-w-none break-words prose-headings:font-bold prose-headings:text-[#20222D] prose-a:text-[#A2CB52] prose-p:mb-6 prose-p:leading-relaxed prose-li:marker:text-[#A2CB52] text-gray-600"
                                dangerouslySetInnerHTML={{ __html: cleanDefinition(term.definition) }}
                            />

                            <div className="mt-12 pt-8 border-t border-gray-100 flex justify-between items-center text-sm text-gray-400">
                                <span>BPO Consultants Encyclopedia</span>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(window.location.href);
                                        alert('Link copied to clipboard!');
                                    }}
                                    className="flex items-center hover:text-[#A2CB52] transition-colors"
                                >
                                    <Share2 size={16} className="mr-2" />
                                    Share Term
                                </button>
                            </div>
                        </div>
                    </div>
                </FadeIn>
            </div>
        </div>
    );
};

export default GlossaryTerm;
